import { useEffect } from "react";

let overlayLockCount = 0;
let previousBodyOverflow = "";
let previousRootOverflow = "";

// LIFO of currently-open Escape-dismissable overlays. Only the top entry
// handles Escape, so a modal opened above a sheet closes alone instead of
// taking the sheet down with it (mount order == stacking order).
const escapeStack: symbol[] = [];

/**
 * Shared overlay behaviour: locks body scroll and closes on Escape.
 * For always-mounted overlays (e.g. Modal) pass `isOpen = true`.
 *
 * `escDismisses` (default true) gates the Escape handler. Set it false on a
 * sheet that is covered by another sheet so Esc dismisses only the topmost —
 * the module-level stack additionally ensures that when several dismissable
 * overlays are open (e.g. a confirm modal above a sheet), one Escape press
 * closes only the most recently opened one.
 */
export function useOverlay(
  isOpen: boolean,
  onClose: () => void,
  escDismisses = true,
): void {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (overlayLockCount === 0) {
      previousBodyOverflow = document.body.style.overflow;
      previousRootOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    }
    overlayLockCount += 1;
    return () => {
      overlayLockCount = Math.max(0, overlayLockCount - 1);
      if (overlayLockCount === 0) {
        document.body.style.overflow = previousBodyOverflow;
        document.documentElement.style.overflow = previousRootOverflow;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !escDismisses) {
      return;
    }
    const token = Symbol("overlay");
    escapeStack.push(token);
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && escapeStack.at(-1) === token) {
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => {
      const index = escapeStack.indexOf(token);
      if (index !== -1) {
        escapeStack.splice(index, 1);
      }
      window.removeEventListener("keydown", handler);
    };
  }, [isOpen, onClose, escDismisses]);
}
